<template>
  <div class="gantt-chart-container">
    <!-- 头部控制区域 -->
    <div class="header-controls">
      <div class="left-controls">
        <h2>项目甘特图</h2>
      </div>
      <div class="date-controls">
        <el-button size="small" icon="el-icon-location" @click="resetToCurrentWeek">定位到本周</el-button>
        <el-button size="small" @click="previousMonth">上个月</el-button>
        <span class="current-date">{{ currentMonthText }}</span>
        <el-button size="small" @click="nextMonth">下个月</el-button>
      </div>
    </div>

    <!-- 甘特图主体 -->
    <div class="gantt-chart">
      <!-- 时间轴和项目列表的统一容器 -->
      <div class="gantt-wrapper">
        <!-- 项目名称列 - 固定不滚动 -->
        <div class="project-column">
          <div class="project-column-header">
            <span>项目</span>
            <div>
              <el-button
                type="text"
                icon="el-icon-plus"
                size="small"
                @click="handleCreateProject"
                class="add-project-btn"
                title="创建项目"
              ></el-button>
              <!-- TODO：后端增加sort排序字段 -->
              <!-- <span @click="handleToggleSort">排序/取消排序</span> -->
            </div>
          </div>
          <div class="project-list" ref="projectList" @scroll="handleProjectScroll">
            <div
              v-for="project in projects"
              :key="project.id"
              class="project-name-cell"
              @click="handleEditProject(project)"
            >
              <div class="project-title">{{ project.name }}</div>
              <div class="project-info">{{ project.description }}</div>
            </div>
          </div>
        </div>

        <!-- 时间轴和任务网格 - 统一滚动 -->
        <div class="timeline-scroll-wrapper" ref="timelineWrapper" @scroll="handleTimelineScroll">
          <div class="timeline-content" :style="{ width: timelineWidth + 'px' }">
            <!-- 时间轴 -->
            <div class="timeline-header">
              <!-- 周行 -->
              <div class="week-row">
                <div
                  v-for="week in visibleWeeks"
                  :key="week.key"
                  class="week-cell"
                  :class="{ 'current-week': week.isCurrent }"
                  :style="{ left: week.left + 'px', width: weekWidth + 'px' }"
                  :title="week.fullLabel"
                >
                  <div class="week-label">{{ week.label }}</div>
                  <!-- 周维度总进度条 -->
                  <div class="week-total-progress">
                    <div class="week-progress-bar">
                      <div
                        class="week-progress-fill"
                        :style="{ width: getWeekTotalProgress(week) + '%' }"
                      ></div>
                    </div>
                    <div class="week-progress-text">{{ getWeekTotalProgressText(week) }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 任务网格 -->
            <div class="gantt-body">
              <div v-for="project in projects" :key="project.id" class="project-row">
                <div class="timeline-grid" :style="{ width: timelineWidth + 'px' }">
                  <!-- 周网格 -->
                  <div
                    v-for="week in visibleWeeks"
                    :key="week.key"
                    class="grid-cell"
                    :class="{ 
                      'has-tasks': getWeekTasks(project, week).length > 0,
                      'all-completed': isWeekAllCompleted(project, week) && !isWeekBeforeCurrentWeek(week),
                      'past-week': isWeekBeforeCurrentWeek(week) && !isWeekAllCompleted(project, week),
                      'past-week-completed': isWeekBeforeCurrentWeek(week) && isWeekAllCompleted(project, week)
                    }"
                    :style="{ left: week.left + 'px', width: weekWidth + 'px' }"
                    @click="handleCellClick(project, week, $event)"
                  >
                    <!-- 单元格内的任务列表 -->
                    <div class="cell-tasks">
                      <div
                        v-for="task in getWeekTasks(project, week)"
                        :key="`${project.id}-${week.key}-${task.id}`"
                        class="cell-task-item"
                      >
                        <!-- 本周之前的任务显示圆点 -->
                        <div
                          v-if="isTaskBeforeCurrentWeek(task)"
                          class="task-dot-item"
                          @click.stop="handleTaskItemClick(project, week, task, $event)"
                        >
                          <span
                            class="task-dot"
                            :class="task.completed ? 'completed' : 'incomplete'"
                          ></span>
                          <span class="task-text">{{ task.name }}</span>
                        </div>
                        <!-- 本周及以后的任务显示checkbox -->
                        <div
                          v-else
                          class="task-checkbox-item"
                          @click.stop="handleTaskItemClick(project, week, task, $event)"
                        >
                          <span class="checkbox_content" @click.stop>
                            <el-checkbox
                              v-model="task.completed"
                              @change="handleTaskComplete(task)"
                              size="small"
                            ></el-checkbox>
                          </span>
                          <span class="task-text-two">{{ task.name }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 今日参考线 -->
                  <div
                    class="today-line"
                    :style="{ left: todayPosition + 'px' }"
                    v-if="todayPosition >= 0 && todayPosition <= timelineWidth"
                    :title="'今天: ' + formatDate(new Date())"
                    @click="handleTodayLineClick"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 今日浮窗 -->
    <transition name="popover-fade">
      <div
        v-if="todayPopover.visible"
        class="today-popover"
        :style="{ left: todayPopover.x + 'px', top: todayPopover.y + 'px' }"
        @click.stop
      >
        <div class="popover-header">
          <i class="el-icon-date"></i>
          <span>今天</span>
        </div>
        <div class="popover-body">
          <div class="date-display">{{ todayPopover.date }}</div>
          <div class="weekday-display">星期{{ todayPopover.weekDay }}</div>
        </div>
      </div>
    </transition>

    <!-- 创建项目抽屉 -->
    <common-drawer
      :visible.sync="projectDrawerVisible"
      title="创建项目"
      size="600px"
      @confirm="handleSaveProject"
      @cancel="projectDrawerVisible = false"
    >
      <el-form :model="newProject" label-width="100px" size="small">
        <el-form-item label="项目名称">
          <el-input v-model="newProject.name" placeholder="请输入项目名称"></el-input>
        </el-form-item>
        <el-form-item label="项目描述">
          <el-input
            v-model="newProject.description"
            type="textarea"
            :rows="4"
            placeholder="请输入项目描述"
          ></el-input>
        </el-form-item>
      </el-form>
    </common-drawer>

    <!-- 编辑项目抽屉 -->
    <common-drawer
      :visible.sync="editProjectDrawerVisible"
      title="编辑项目"
      size="600px"
      @confirm="handleUpdateProject"
      @cancel="editProjectDrawerVisible = false"
    >
      <el-form :model="editProjectForm" label-width="100px" size="small">
        <el-form-item label="项目名称">
          <el-input v-model="editProjectForm.name" placeholder="请输入项目名称"></el-input>
        </el-form-item>
        <el-form-item label="项目描述">
          <el-input
            v-model="editProjectForm.description"
            type="textarea"
            :rows="4"
            placeholder="请输入项目描述"
          ></el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <div style="display: flex; justify-content: space-between; width: 100%;">
          <el-button type="danger" size="small" @click="handleConfirmDeleteProject">删除项目</el-button>
          <div>
            <el-button size="small" @click="editProjectDrawerVisible = false">取消</el-button>
            <el-button type="primary" size="small" @click="handleUpdateProject">确定</el-button>
          </div>
        </div>
      </template>
    </common-drawer>

    <!-- 任务管理抽屉 -->
    <common-drawer
      :visible.sync="drawerVisible"
      :title="dialogTitle"
      size="600px"
      @confirm="handleDrawerConfirm"
      @cancel="handleDrawerCancel"
      :show-footer="false"
    >
      <div class="drawer-content">
        <div class="drawer-actions">
          <el-button size="small" type="primary" icon="el-icon-plus" @click="handleAddTask">新增任务</el-button>
        </div>

        <div class="task-list">
          <div
            v-for="(task, index) in selectedTasks"
            :key="index"
            class="task-item"
            :class="{ 
              'movable': movableTaskIndex === index,
              'selected': task.id === selectedTaskId
            }"
            :draggable="movableTaskIndex === index"
            @click="handleTaskItemSelection(task)"
            @dragstart="handleDragStart(index, $event)"
            @dragover="handleDragOver($event)"
            @drop="handleDrop(index, $event)"
            @dragend="handleDragEnd"
          >
            <div v-if="task.editing" class="task-edit-form">
              <el-form :model="task" label-width="80px" size="small">
                <el-form-item label="任务名称">
                  <el-input v-model="task.name" placeholder="请输入任务名称"></el-input>
                </el-form-item>
                <el-form-item label="任务日期">
                  <el-date-picker
                    v-model="task.date"
                    type="date"
                    placeholder="选择日期"
                    value-format="yyyy-MM-dd"
                    style="width: 100%;"
                  ></el-date-picker>
                </el-form-item>
                <el-form-item label="状态">
                  <el-select v-model="task.status" placeholder="请选择状态" style="width: 100%;">
                    <el-option label="未完成" value="未完成"></el-option>
                    <el-option label="已完成" value="已完成"></el-option>
                  </el-select>
                </el-form-item>
                <el-form-item>
                  <el-button type="success" size="small" @click.stop="handleSaveTask(task)">保存</el-button>
                  <el-button size="small" @click.stop="handleCancelEdit(task)">取消</el-button>
                </el-form-item>
              </el-form>
            </div>
            <div v-else class="task-view">
              <div class="task-header">
                <div class="task-name">
                  <i
                    class="el-icon-document copy-icon"
                    title="点击复制任务名称"
                    @click="handleCopyTaskName(task.name)"
                  ></i>
                  {{ task.name }}
                </div>
              </div>
              <div class="task-details">
                <div class="task-detail-item">
                  <span class="label">日期:</span>
                  <span class="value">{{ task.date }}</span>
                </div>
                <div class="task-detail-item">
                  <span class="label">状态:</span>
                  <el-tag :type="getStatusType(task.status)" size="mini">{{ task.status }}</el-tag>
                </div>
              </div>
              <div class="task-actions">
                <el-button
                  v-if="movableTaskIndex !== index"
                  size="mini"
                  type="text"
                  icon="el-icon-rank"
                  @click.stop="handleToggleMovable(index)"
                >移动</el-button>
                <el-button
                  v-else
                  size="mini"
                  type="text"
                  icon="el-icon-close"
                  @click.stop="handleToggleMovable(null)"
                  style="color: #f56c6c;"
                >取消移动</el-button>
                <el-button
                  size="mini"
                  type="text"
                  icon="el-icon-edit"
                  @click.stop="handleEditTask(task)"
                >编辑</el-button>
                <el-button
                  size="mini"
                  type="text"
                  icon="el-icon-delete"
                  @click.stop="handleDeleteTask(task)"
                  style="color: #f56c6c;"
                >删除</el-button>
              </div>
            </div>
          </div>

          <div v-if="selectedTasks.length === 0" class="no-tasks">
            <i class="el-icon-document-delete"></i>
            <p>该时间段暂无任务</p>
          </div>
        </div>
      </div>
    </common-drawer>
  </div>
</template>

<script>
import CommonDrawer from "@/components/drawer/index.vue";
import {
  selectApi,
  addApi,
  updateApi,
  deleteApi
} from "@/services/sysTask/index.js";

export default {
  name: "GanttChart",
  components: {
    CommonDrawer
  },
  data() {
    const today = new Date();
    const formatDate = date => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    // 计算上一周的日期（今天往回7天）
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);

    return {
      // 滚动同步标志
      scrollLock: false,
      // 是否为初始状态（未点击过月份切换按钮）
      isInitialState: true,
      // 当前显示的月份（默认为上一周所在月份）
      currentMonth: new Date(lastWeek),
      // 每周的宽度（像素）
      weekWidth: 240,
      // 项目数据
      projects: [],
      // 任务抽屉相关
      drawerVisible: false,
      dialogTitle: "",
      selectedTasks: [],
      currentProject: null,
      currentWeek: null,
      // 项目抽屉相关
      projectDrawerVisible: false,
      newProject: {
        name: "",
        description: ""
      },
      // 项目编辑相关
      editProjectDrawerVisible: false,
      editingProject: null,
      editProjectForm: {
        name: "",
        description: ""
      },
      // 今日浮窗相关
      todayPopover: {
        visible: false,
        x: 0,
        y: 0,
        date: "",
        weekDay: ""
      },
      // 拖拽相关
      dragStartIndex: null,
      // 移动模式（记录哪个任务处于可移动状态）
      movableTaskIndex: null,
      // 当前选中的任务ID
      selectedTaskId: null
    };
  },
  computed: {
    // 当前月份文本
    currentMonthText() {
      const year = this.currentMonth.getFullYear();
      const month = this.currentMonth.getMonth() + 1;
      return `${year}年${month}月`;
    },
    // 时间轴宽度
    timelineWidth() {
      return this.visibleWeeks.length * this.weekWidth;
    },
    // 可见的周列表
    visibleWeeks() {
      const weeks = [];

      if (this.isInitialState) {
        // 初始状态：当前周在第二列，显示一个月的数据
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // 找到今天所在周的周一
        const todayWeekDay = today.getDay();
        const currentWeekStart = new Date(today);
        currentWeekStart.setDate(
          currentWeekStart.getDate() -
            (todayWeekDay === 0 ? 6 : todayWeekDay - 1)
        );

        // 从当前周的前1周开始（这样当前周就是第2列）
        const firstWeekStart = new Date(currentWeekStart);
        firstWeekStart.setDate(firstWeekStart.getDate() - 7);

        // 显示总共5周（前1周 + 当前周 + 后3周，约5周一个月）
        let weekStartDate = new Date(firstWeekStart);

        for (let weekIndex = 0; weekIndex < 5; weekIndex++) {
          const weekEnd = new Date(weekStartDate);
          weekEnd.setDate(weekEnd.getDate() + 6);

          const startMonth = weekStartDate.getMonth() + 1;
          const startDay = weekStartDate.getDate();
          const endMonth = weekEnd.getMonth() + 1;
          const endDay = weekEnd.getDate();

          // 格式化日期
          const startLabel = `${String(startMonth).padStart(2, "0")}月${String(
            startDay
          ).padStart(2, "0")}日`;
          const endLabel = `${String(endMonth).padStart(2, "0")}月${String(
            endDay
          ).padStart(2, "0")}日`;

          weeks.push({
            key: `week-${weekIndex}`,
            label: `${startLabel}-${endLabel}`,
            fullLabel: `${startLabel} - ${endLabel}`,
            startDate: new Date(weekStartDate),
            endDate: weekEnd,
            left: weekIndex * this.weekWidth,
            isCurrent: this.isCurrentWeek(weekStartDate)
          });

          weekStartDate.setDate(weekStartDate.getDate() + 7);
        }
      } else {
        // 非初始状态：按照选中的月份显示整个月的数据
        const monthStart = new Date(this.currentMonth);
        monthStart.setDate(1);
        monthStart.setHours(0, 0, 0, 0);

        const monthEnd = new Date(this.currentMonth);
        monthEnd.setMonth(monthEnd.getMonth() + 1);
        monthEnd.setDate(0);
        monthEnd.setHours(23, 59, 59, 999);

        // 找到月初所在周的周一
        const firstDay = monthStart.getDay();
        const weekStartDate = new Date(monthStart);
        weekStartDate.setDate(
          weekStartDate.getDate() - (firstDay === 0 ? 6 : firstDay - 1)
        );

        let currentWeekStart = new Date(weekStartDate);
        let weekIndex = 0;

        // 生成整个月的周
        while (currentWeekStart <= monthEnd) {
          const weekEnd = new Date(currentWeekStart);
          weekEnd.setDate(weekEnd.getDate() + 6);

          const startMonth = currentWeekStart.getMonth() + 1;
          const startDay = currentWeekStart.getDate();
          const endMonth = weekEnd.getMonth() + 1;
          const endDay = weekEnd.getDate();

          // 格式化日期
          const startLabel = `${String(startMonth).padStart(2, "0")}月${String(
            startDay
          ).padStart(2, "0")}日`;
          const endLabel = `${String(endMonth).padStart(2, "0")}月${String(
            endDay
          ).padStart(2, "0")}日`;

          weeks.push({
            key: `week-${weekIndex}`,
            label: `${startLabel}-${endLabel}`,
            fullLabel: `${startLabel} - ${endLabel}`,
            startDate: new Date(currentWeekStart),
            endDate: weekEnd,
            left: weekIndex * this.weekWidth,
            isCurrent: this.isCurrentWeek(currentWeekStart)
          });

          currentWeekStart.setDate(currentWeekStart.getDate() + 7);
          weekIndex++;
        }
      }

      return weeks;
    },
    // 今日位置
    todayPosition() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (this.visibleWeeks.length === 0) return -1;

      const firstWeekStart = this.visibleWeeks[0].startDate;
      const diffTime = today - firstWeekStart;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return (diffDays / 7) * this.weekWidth;
    }
  },
  mounted() {
    this.loadProjects();
  },
  methods: {
    // 加载项目列表
    async loadProjects() {
      try {
        const res = await selectApi({ pageNumber: 1, pageSize: 1000 });
        if (res && res.data) {
          this.projects = res.data.map(item => ({
            ...item,
            tasks: item.tasks ? JSON.parse(item.tasks) : []
          }));
        }
      } catch (error) {
        console.error("加载项目列表失败:", error);
        this.$message.error("加载项目列表失败");
      }
    },
    // 判断是否是当前周
    isCurrentWeek(weekStart) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      return today >= weekStart && today <= weekEnd;
    },
    // 判断任务是否在本周之前
    isTaskBeforeCurrentWeek(task) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // 找到今天所在周的周一
      const todayWeekDay = today.getDay();
      const currentWeekStart = new Date(today);
      currentWeekStart.setDate(
        currentWeekStart.getDate() - (todayWeekDay === 0 ? 6 : todayWeekDay - 1)
      );
      currentWeekStart.setHours(0, 0, 0, 0);

      const taskDate = new Date(task.date);
      taskDate.setHours(0, 0, 0, 0);

      return taskDate < currentWeekStart;
    },
    // 判断周是否在本周之前
    isWeekBeforeCurrentWeek(week) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // 找到今天所在周的周一
      const todayWeekDay = today.getDay();
      const currentWeekStart = new Date(today);
      currentWeekStart.setDate(
        currentWeekStart.getDate() - (todayWeekDay === 0 ? 6 : todayWeekDay - 1)
      );
      currentWeekStart.setHours(0, 0, 0, 0);

      const weekEnd = new Date(week.endDate);
      weekEnd.setHours(0, 0, 0, 0);

      return weekEnd < currentWeekStart;
    },
    // 上个月
    previousMonth() {
      this.isInitialState = false;
      const newMonth = new Date(
        this.currentMonth.getFullYear(),
        this.currentMonth.getMonth() - 1,
        1
      );
      this.currentMonth = newMonth;

      // 重置滚动位置到起点
      this.$nextTick(() => {
        const timelineWrapper = this.$refs.timelineWrapper;
        const projectList = this.$refs.projectList;
        if (timelineWrapper) {
          timelineWrapper.scrollLeft = 0;
          timelineWrapper.scrollTop = 0;
        }
        if (projectList) {
          projectList.scrollTop = 0;
        }
      });
    },
    // 下个月
    nextMonth() {
      this.isInitialState = false;
      const newMonth = new Date(
        this.currentMonth.getFullYear(),
        this.currentMonth.getMonth() + 1,
        1
      );
      this.currentMonth = newMonth;

      // 重置滚动位置到起点
      this.$nextTick(() => {
        const timelineWrapper = this.$refs.timelineWrapper;
        const projectList = this.$refs.projectList;
        if (timelineWrapper) {
          timelineWrapper.scrollLeft = 0;
          timelineWrapper.scrollTop = 0;
        }
        if (projectList) {
          projectList.scrollTop = 0;
        }
      });
    },
    // 重置到初始状态（定位到本周）
    resetToCurrentWeek() {
      this.isInitialState = true;
      const today = new Date();
      const currentMonthStart = new Date(
        today.getFullYear(),
        today.getMonth(),
        1
      );
      this.currentMonth = currentMonthStart;

      // 重置滚动位置到起点
      this.$nextTick(() => {
        const timelineWrapper = this.$refs.timelineWrapper;
        const projectList = this.$refs.projectList;
        if (timelineWrapper) {
          timelineWrapper.scrollLeft = 0;
          timelineWrapper.scrollTop = 0;
        }
        if (projectList) {
          projectList.scrollTop = 0;
        }
      });
    },
    // 打开创建项目抽屉
    handleCreateProject() {
      this.newProject = {
        name: "",
        description: ""
      };
      this.projectDrawerVisible = true;
    },
    // 保存新项目
    async handleSaveProject() {
      if (!this.newProject.name) {
        this.$message.warning("请输入项目名称");
        return;
      }

      try {
        const params = {
          name: this.newProject.name,
          description: this.newProject.description || "",
          tasks: JSON.stringify([])
        };

        const res = await addApi(params);
        if (res && res.code === 200) {
          this.$message.success("项目创建成功");
          this.projectDrawerVisible = false;
          this.loadProjects();
        }
      } catch (error) {
        console.error("创建项目失败:", error);
        this.$message.error("创建项目失败");
      }
    },
    // 打开编辑项目抽屉
    handleEditProject(project) {
      this.editingProject = project;
      this.editProjectForm = {
        name: project.name,
        description: project.description
      };
      this.editProjectDrawerVisible = true;
    },
    // 更新项目
    async handleUpdateProject() {
      if (!this.editProjectForm.name) {
        this.$message.warning("请输入项目名称");
        return;
      }

      try {
        const params = {
          id: this.editingProject.id,
          name: this.editProjectForm.name,
          description: this.editProjectForm.description,
          tasks: JSON.stringify(this.editingProject.tasks || [])
        };

        const res = await updateApi(params);
        if (res && res.code === 200) {
          this.$message.success("项目更新成功");
          this.editProjectDrawerVisible = false;
          this.loadProjects();
        }
      } catch (error) {
        console.error("更新项目失败:", error);
      }
    },
    // 确认删除项目
    handleConfirmDeleteProject() {
      this.$confirm(
        "删除项目将同时删除该项目下的所有任务,确定要删除吗?",
        "警告",
        {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning"
        }
      )
        .then(async () => {
          try {
            const res = await deleteApi({ id: this.editingProject.id });
            if (res && res.code === 200) {
              this.$message.success("项目删除成功");
              this.editProjectDrawerVisible = false;
              this.loadProjects();
            }
          } catch (error) {
            console.error("删除项目失败:", error);
          }
        })
        .catch(() => {});
    },
    // 格式化日期
    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    // 获取某周的任务列表
    getWeekTasks(project, week) {
      const weekStart = week.startDate;
      const weekEnd = week.endDate;

      return project.tasks.filter(task => {
        const taskDate = new Date(task.date);
        taskDate.setHours(0, 0, 0, 0);

        return taskDate >= weekStart && taskDate <= weekEnd;
      });
    },
    // 判断某周的任务是否全部完成
    isWeekAllCompleted(project, week) {
      const tasks = this.getWeekTasks(project, week);
      if (tasks.length === 0) return false;
      return tasks.every(task => task.completed);
    },
    // 处理任务完成状态变化
    async handleTaskComplete(task) {
      // 根据勾选状态同步任务状态
      task.status = task.completed ? "已完成" : "未完成";

      // 找到该任务所属的项目并更新到服务器
      const project = this.projects.find(p =>
        p.tasks.some(t => t.id === task.id)
      );

      if (project) {
        try {
          const params = {
            id: project.id,
            tasks: JSON.stringify(project.tasks)
          };
          const res = await updateApi(params);
          if (res && res.code === 200) {
            // 静默更新，不显示提示
          }
        } catch (error) {
          console.error("更新任务状态失败:", error);
        }
      }

      // 触发视图更新
      this.$forceUpdate();
    },
    // 计算周任务完成进度百分比
    getWeekProgress(project, week) {
      const tasks = this.getWeekTasks(project, week);
      if (tasks.length === 0) return 0;
      const completedCount = tasks.filter(task => task.completed).length;
      return Math.round((completedCount / tasks.length) * 100);
    },
    // 获取周任务进度文本
    getWeekProgressText(project, week) {
      const tasks = this.getWeekTasks(project, week);
      const completedCount = tasks.filter(task => task.completed).length;
      return `${completedCount}/${tasks.length}`;
    },
    // 计算周维度总进度（所有项目在该周的任务汇总）
    getWeekTotalProgress(week) {
      let totalTasks = 0;
      let completedTasks = 0;

      this.projects.forEach(project => {
        const tasks = this.getWeekTasks(project, week);
        totalTasks += tasks.length;
        completedTasks += tasks.filter(task => task.completed).length;
      });

      if (totalTasks === 0) return 0;
      return Math.round((completedTasks / totalTasks) * 100);
    },
    // 获取周维度总进度文本
    getWeekTotalProgressText(week) {
      let totalTasks = 0;
      let completedTasks = 0;

      this.projects.forEach(project => {
        const tasks = this.getWeekTasks(project, week);
        totalTasks += tasks.length;
        completedTasks += tasks.filter(task => task.completed).length;
      });

      return `${completedTasks}/${totalTasks}`;
    },
    // 处理单元格点击
    handleCellClick(project, week, event, selectedTaskId = null) {
      // 筛选该周的任务
      const tasksInWeek = this.getWeekTasks(project, week);

      this.currentProject = project;
      this.currentWeek = week;
      this.dialogTitle = `${project.name} - ${week.fullLabel}`;
      this.selectedTasks = JSON.parse(JSON.stringify(tasksInWeek));

      // 设置选中的任务ID
      this.selectedTaskId = selectedTaskId;

      this.drawerVisible = true;
    },
    // 处理任务项点击
    handleTaskItemClick(project, week, task, event) {
      // 打开抽屉并选中该任务
      this.handleCellClick(project, week, event, task.id);
    },
    // 处理任务项选中切换
    handleTaskItemSelection(task) {
      // 如果点击的是当前已选中的任务，则取消选中
      if (this.selectedTaskId === task.id) {
        this.selectedTaskId = null;
      } else {
        // 否则选中该任务
        this.selectedTaskId = task.id;
      }
    },
    // 抽屉确认
    handleDrawerConfirm() {
      this.drawerVisible = false;
      this.selectedTaskId = null;
    },
    // 抽屉取消
    handleDrawerCancel() {
      this.drawerVisible = false;
      this.selectedTaskId = null;
    },
    // 新增任务
    handleAddTask() {
      if (!this.currentWeek) return;

      const newTask = {
        name: "新任务",
        date: this.formatDate(this.currentWeek.startDate),
        status: "未完成",
        completed: false,
        editing: true
      };

      this.selectedTasks.push(newTask);
    },
    // 编辑任务
    handleEditTask(task) {
      task.editing = true;
      task.oldData = { ...task };
      this.$forceUpdate();
    },
    // 保存任务
    async handleSaveTask(task) {
      if (!task.name || !task.date) {
        this.$message.warning("请填写完整信息");
        return;
      }

      // 同步completed和status状态
      if (task.status === "已完成") {
        task.completed = true;
      } else if (task.status === "未完成") {
        task.completed = false;
      }

      // 如果没有id，说明是新任务，生成id
      if (!task.id) {
        task.id = `task-${Date.now()}`;
      }

      task.editing = false;

      // 更新到原项目数据
      if (this.currentProject) {
        const existingTask = this.currentProject.tasks.find(
          t => t.id === task.id
        );
        if (existingTask) {
          Object.assign(existingTask, task);
        } else {
          // 新任务
          this.currentProject.tasks.push({ ...task });
        }

        // 更新到服务器
        try {
          const params = {
            id: this.currentProject.id,
            tasks: JSON.stringify(this.currentProject.tasks)
          };
          const res = await updateApi(params);
          if (res && res.code === 200) {
            this.$message.success("保存成功");
            this.loadProjects();
          } else {
            this.$message.error(res.msg || "保存失败");
          }
        } catch (error) {
          console.error("保存任务失败:", error);
          this.$message.error("保存任务失败");
        }
      }

      this.$forceUpdate();
    },
    // 取消编辑
    handleCancelEdit(task) {
      console.log("取消编辑任务:", task);
      if (!task.id) {
        // 没有id说明是新任务，取消则删除
        const index = this.selectedTasks.findIndex(t => t === task);
        if (index > -1) {
          this.selectedTasks.splice(index, 1);
        }
      } else {
        // 恢复旧数据
        if (task.oldData) {
          Object.assign(task, task.oldData);
        }
        task.editing = false;
      }
      this.$forceUpdate();
    },
    // 删除任务
    handleDeleteTask(task) {
      this.$confirm("确定要删除这个任务吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      })
        .then(async () => {
          // 从弹窗列表中删除
          const dialogIndex = this.selectedTasks.findIndex(
            t => t.id === task.id
          );
          if (dialogIndex > -1) {
            this.selectedTasks.splice(dialogIndex, 1);
          }

          // 从项目数据中删除
          if (this.currentProject) {
            const projectIndex = this.currentProject.tasks.findIndex(
              t => t.id === task.id
            );
            if (projectIndex > -1) {
              this.currentProject.tasks.splice(projectIndex, 1);
            }

            // 更新到服务器
            try {
              const params = {
                id: this.currentProject.id,
                tasks: JSON.stringify(this.currentProject.tasks)
              };
              const res = await updateApi(params);
              if (res && res.code === 200) {
                this.$message.success("删除成功");
                this.loadProjects();
              } else {
                this.$message.error(res.msg || "删除失败");
              }
            } catch (error) {
              console.error("删除任务失败:", error);
              this.$message.error("删除任务失败");
            }
          }

          this.$forceUpdate();
        })
        .catch(() => {});
    },
    // 获取状态标签类型
    getStatusType(status) {
      if (status === "已完成") return "success";
      if (status === "未完成") return "warning";
      return "primary";
    },
    // 处理项目列表滚动
    handleProjectScroll(event) {
      if (this.scrollLock) return;
      this.scrollLock = true;

      const projectList = this.$refs.projectList;
      const timelineWrapper = this.$refs.timelineWrapper;

      if (timelineWrapper && projectList) {
        timelineWrapper.scrollTop = projectList.scrollTop;
      }

      this.$nextTick(() => {
        this.scrollLock = false;
      });
    },
    // 处理时间轴滚动
    handleTimelineScroll(event) {
      if (this.scrollLock) return;
      this.scrollLock = true;

      const projectList = this.$refs.projectList;
      const timelineWrapper = this.$refs.timelineWrapper;

      if (projectList && timelineWrapper) {
        projectList.scrollTop = timelineWrapper.scrollTop;
      }

      this.$nextTick(() => {
        this.scrollLock = false;
      });
    },
    // 处理今日参考线点击
    handleTodayLineClick(event) {
      const today = new Date();
      const dateStr = this.formatDate(today);
      const weekDay = ["日", "一", "二", "三", "四", "五", "六"][
        today.getDay()
      ];

      // 设置浮窗位置（相对于视口）
      this.todayPopover = {
        visible: true,
        x: event.clientX + 10,
        y: event.clientY - 80,
        date: dateStr,
        weekDay: weekDay
      };

      // 3秒后自动关闭
      setTimeout(() => {
        this.todayPopover.visible = false;
      }, 3000);
    },
    // 切换移动模式
    handleToggleMovable(index) {
      this.movableTaskIndex = index;
      // if (index !== null) {
      //   this.$message.info("已开启移动模式，可以拖拽调整任务顺序");
      // }
    },
    // 拖拽开始
    handleDragStart(index, event) {
      this.dragStartIndex = index;
      event.dataTransfer.effectAllowed = "move";
      event.target.style.opacity = "0.5";
    },
    // 复制任务名称
    handleCopyTaskName(taskName) {
      // 创建临时文本域
      const textarea = document.createElement("textarea");
      textarea.value = taskName;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();

      try {
        document.execCommand("copy");
        this.$message.success("任务名称已复制");
      } catch (err) {
        this.$message.error("复制失败");
      }

      document.body.removeChild(textarea);
    },
    // 拖拽经过
    handleDragOver(event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
    },
    // 放下
    async handleDrop(dropIndex, event) {
      event.preventDefault();

      if (this.dragStartIndex === null || this.dragStartIndex === dropIndex) {
        return;
      }

      // 调整任务顺序
      const draggedTask = this.selectedTasks[this.dragStartIndex];
      this.selectedTasks.splice(this.dragStartIndex, 1);
      this.selectedTasks.splice(dropIndex, 0, draggedTask);

      // 同步更新到项目数据中
      if (this.currentProject && this.currentWeek) {
        // 获取当前周的所有任务ID
        const weekTaskIds = this.selectedTasks.map(t => t.id);

        // 创建一个新的任务数组，按照拖拽后的顺序排列
        const otherTasks = this.currentProject.tasks.filter(t => {
          const taskDate = new Date(t.date);
          taskDate.setHours(0, 0, 0, 0);
          const weekStart = this.currentWeek.startDate;
          const weekEnd = this.currentWeek.endDate;
          return taskDate < weekStart || taskDate > weekEnd;
        });

        // 合并：其他周的任务 + 当前周按新顺序排列的任务
        this.currentProject.tasks = [...otherTasks, ...this.selectedTasks];

        // 保存到服务器
        try {
          const params = {
            id: this.currentProject.id,
            tasks: JSON.stringify(this.currentProject.tasks)
          };
          const res = await updateApi(params);
          if (res && res.code === 200) {
            this.loadProjects();
          }
        } catch (error) {
          console.error("更新任务顺序失败:", error);
          this.$message.error("更新任务顺序失败");
        }
      }

      this.$forceUpdate();
    },
    // 拖拽结束
    handleDragEnd(event) {
      event.target.style.opacity = "1";
      this.dragStartIndex = null;
      // 拖拽结束后自动取消移动模式
      this.movableTaskIndex = null;
    }
  }
};
</script>

<style scoped lang="less">
.gantt-chart-container {
  padding: 20px;
  background: #fff;
  height: calc(100vh - 50px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.header-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;

  .left-controls {
    display: flex;
    align-items: center;
    gap: 15px;

    h2 {
      margin: 0;
      font-size: 20px;
      color: #333;
    }
  }

  .date-controls {
    display: flex;
    align-items: center;
    gap: 15px;

    .current-date {
      font-size: 16px;
      font-weight: 500;
      color: #409eff;
      min-width: 100px;
      text-align: center;
    }
  }
}

.gantt-chart {
  flex: 1;
  overflow: hidden;
  border-bottom: 2px solid #409eff;
  //   border-radius: 4px;
  //   border: 1px solid #e0e0e0;
}

.gantt-wrapper {
  display: flex;
  height: 100%;
}

.project-column {
  width: 200px;
  min-width: 200px;
  flex-shrink: 0;
  background: #fff;
  border-right: 2px solid #e0e0e0;
}

.project-column-header {
  height: 50px;
  background: #f5f7fa;
  border-bottom: 2px solid #409eff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 15px;
  font-weight: 600;
  font-size: 14px;
  color: #333;

  .add-project-btn {
    font-size: 18px;
    color: #409eff;
    padding: 5px;
    background: rgba(64, 158, 255, 0.1);
    &:hover {
      color: #ffffff;
      background: rgba(64, 158, 255, 0.5);
    }
  }
}

.project-list {
  overflow-y: auto;
  height: calc(100% - 50px);

  /* 隐藏滚动条 */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */

  &::-webkit-scrollbar {
    display: none; /* Chrome Safari */
  }
}

.project-name-cell {
  padding: 15px;
  border-bottom: 1px solid #e0e0e0;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #ecf5ff;
    box-shadow: inset 3px 0 0 #409eff;
  }

  .project-title {
    font-weight: 600;
    font-size: 14px;
    color: #303133;
    margin-bottom: 8px;
  }

  .project-info {
    font-size: 12px;
    color: #909399;
  }
}

.timeline-scroll-wrapper {
  flex: 1;
  overflow-x: auto;
  overflow-y: auto;

  /* 隐藏滚动条 */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */

  &::-webkit-scrollbar {
    display: none; /* Chrome Safari */
  }
}

.timeline-content {
  position: relative;
  min-width: 100%;
}

.timeline-header {
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 10;
  border-bottom: 2px solid #409eff;
}

.week-row {
  display: flex;
  height: 50px;
  position: relative;
}

.week-cell {
  position: absolute;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  border-right: 1px solid #e0e0e0;
  font-size: 12px;
  color: #909399;
  transition: background 0.2s;
  cursor: pointer;
  padding: 8px 5px;

  &:hover {
    background: #e6f7ff;
  }

  &.current-week {
    background: #ecf5ff;
    color: #409eff;
    font-weight: 600;
  }

  .week-label {
    margin-bottom: 6px;
    text-align: center;
  }

  .week-total-progress {
    width: 100%;
    padding: 0 8px;

    .week-progress-bar {
      height: 6px;
      background: #e0e0e0;
      border-radius: 3px;
      overflow: hidden;
      margin-bottom: 3px;

      .week-progress-fill {
        height: 100%;
        background: linear-gradient(90deg, #409eff 0%, #66b1ff 100%);
        transition: width 0.3s ease;
      }
    }

    .week-progress-text {
      font-size: 10px;
      color: #909399;
      text-align: center;
    }
  }
}

.gantt-body {
  position: relative;
}

.project-row {
  border-bottom: 1px solid #e0e0e0;
  min-height: 120px;
  &:hover {
    background: #fafafa;
  }
}

.timeline-grid {
  position: relative;
  min-height: 120px;
}

.grid-cell {
  position: absolute;
  height: 100%;
  border-right: 1px solid #e0e0e0;
  cursor: pointer;
  transition: background 0.2s;
  padding: 12px;
  overflow-y: auto;
  /* 隐藏滚动条 */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */

  &::-webkit-scrollbar {
    display: none; /* Chrome Safari */
  }

  &:hover {
    background: rgba(64, 158, 255, 0.03);
  }

  &.all-completed {
    background: #a0d8fe86;
  }

  &.past-week {
    background: rgba(0, 0, 0, 0.03);
  }

  &.past-week-completed {
    background: rgba(160, 216, 254, 0.25);
  }
}

.cell-tasks {
  display: flex;
  flex-direction: column;
}

.cell-task-item {
  font-size: 13px;
  padding: 4px 0;
  .task-text {
    color: #606266;
  }
}

//勾选项文字需要自动换行
.task-checkbox-item {
  display: flex;
  align-items: flex-start;
  // white-space: normal;
  // word-wrap: break-word;
  // word-break: break-word;
}
.checkbox_content {
  margin-right: 5px;
  /deep/ .el-checkbox {
    display: flex;
    // padding: 0 !important;
  }
}

.task-text-two {
  display: inline-block;
  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
  color: #606266;
  position: relative;
  top: -2px; //字体行高影响了对齐
  font-size: 13px;
  font-weight: 400;
  letter-spacing: 1px;
}

.task-dot-item {
  display: flex;
  align-items: center;
  gap: 8px;

  .task-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;

    &.completed {
      background: #67c23a;
    }

    &.incomplete {
      background: #f56c6c;
    }
  }
}

/* 今日参考线 */
.today-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  border-left: 3px dashed rgba(245, 108, 108, 0.3);
  z-index: 3;
  cursor: pointer;
}

// 今日浮窗样式
.today-popover {
  position: fixed;
  z-index: 9999;
  //   background: linear-gradient(135deg, #edf3ff 0%, #ffffff 100%);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  padding: 0;
  min-width: 100px;
  color: #303030;
  overflow: hidden;
  background: #edf3ff;

  .popover-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px 0;
    background: rgba(255, 255, 255, 0.1);
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    font-size: 14px;
    font-weight: 600;
    i {
      font-size: 16px;
    }
  }

  .popover-body {
    padding: 0 10px 8px;

    .date-display {
      font-size: 14px;
      font-weight: 700;
      text-align: center;
      letter-spacing: 1px;
    }

    .weekday-display {
      font-size: 14px;
      text-align: center;
      opacity: 0.9;
    }
  }
}

.popover-fade-enter-active,
.popover-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.popover-fade-enter {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}

.popover-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}

// 抽屉样式
.drawer-content {
  padding: 10px 0;
}

.drawer-actions {
  padding: 0 0 20px 0;
  border-bottom: 1px solid #eee;
}

.task-list {
  margin-top: 20px;
}

.task-item {
  margin-bottom: 20px;
  padding: 15px;
  background: #f9f9f9;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  transition: all 0.3s;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-color: #409eff;
  }

  &.selected {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-color: #409eff;
    background: #ecf5ff;
  }

  &.movable {
    cursor: move;
    border-color: #409eff;
    background: #ecf5ff;
    box-shadow: 0 2px 12px rgba(64, 158, 255, 0.3);
  }
}

.task-view {
  position: relative;
  // border: 1px solid red;

  .task-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    .task-name {
      font-size: 15px;
      font-weight: 600;
      color: #303133;
      display: flex;
      align-items: center;

      i {
        margin-right: 6px;
        color: #409eff;
      }

      .copy-icon {
        cursor: pointer;
        transition: color 0.3s;

        &:hover {
          color: #67c23a;
        }
      }
    }
  }

  .task-details {
    .task-detail-item {
      margin-bottom: 8px;
      font-size: 13px;
      color: #606266;

      .label {
        color: #909399;
        margin-right: 8px;
      }

      .value {
        color: #606266;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .task-actions {
    position: absolute;
    bottom: -5px;
    right: 0;
  }
}

.task-edit-form {
  background: #fff;
  padding: 15px;
  border-radius: 4px;
}

.no-tasks {
  text-align: center;
  padding: 60px 20px;
  color: #909399;

  i {
    font-size: 48px;
    margin-bottom: 15px;
    color: #d0d0d0;
  }

  p {
    font-size: 14px;
    margin: 0;
  }
}
</style>
